import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type AdminUpgrade } from '../../api/admin';
import UpgradeEditModal from './UpgradeEditModal';
import {
  Mountain, Droplets, Bug, Building2, Satellite, Waves, TreePine,
  Search, Edit3, Plus, AlertTriangle, ArrowRight, Lock,
} from 'lucide-react';

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  geology: <Mountain className="w-3.5 h-3.5" />,
  atmosphere: <Droplets className="w-3.5 h-3.5" />,
  water: <Waves className="w-3.5 h-3.5" />,
  life: <Bug className="w-3.5 h-3.5" />,
  plant: <TreePine className="w-3.5 h-3.5" />,
  civilisation: <Building2 className="w-3.5 h-3.5" />,
  technology: <Satellite className="w-3.5 h-3.5" />,
};

const CATEGORY_COLORS: Record<string, string> = {
  geology: 'text-amber-400 bg-amber-950/40 border-amber-800/40',
  atmosphere: 'text-sky-400 bg-sky-950/40 border-sky-800/40',
  water: 'text-blue-400 bg-blue-950/40 border-blue-800/40',
  life: 'text-lime-400 bg-lime-950/40 border-lime-800/40',
  plant: 'text-emerald-400 bg-emerald-950/40 border-emerald-800/40',
  civilisation: 'text-orange-400 bg-orange-950/40 border-orange-800/40',
  technology: 'text-violet-400 bg-violet-950/40 border-violet-800/40',
};

const STAGE_NAMES = [
  'Barren Planet',
  'Terraforming',
  'Spark of Life',
  'Plant Life',
  'Complex Life',
  'Civilisation',
  'Technology',
  'Space Technology',
  'Space Exploration',
];

const STAGE_COLORS = [
  'text-slate-400 border-slate-700',
  'text-sky-400 border-sky-700',
  'text-lime-400 border-lime-700',
  'text-emerald-400 border-emerald-700',
  'text-teal-400 border-teal-700',
  'text-orange-400 border-orange-700',
  'text-violet-400 border-violet-700',
  'text-cyan-400 border-cyan-700',
  'text-yellow-400 border-yellow-700',
];

function summarizeEffects(effects: Record<string, unknown>): string[] {
  const tags: string[] = [];
  const stats = (effects.stats || {}) as Record<string, number>;
  const environmental = (effects.environmental || {}) as Record<string, number>;
  const geography = (effects.geography || {}) as Record<string, number>;

  for (const [key, value] of Object.entries(stats)) {
    if (typeof value === 'number') {
      tags.push(`${value >= 0 ? '+' : ''}${value} ${key}`);
    }
  }
  for (const [key, value] of Object.entries(environmental)) {
    if (typeof value === 'number') {
      tags.push(`${value >= 0 ? '+' : ''}${value} ${key}`);
    }
  }
  for (const [key, value] of Object.entries(geography)) {
    if (typeof value === 'number') {
      tags.push(`${value >= 0 ? '+' : ''}${value} ${key}`);
    }
  }
  return tags.slice(0, 4);
}

function hasLinearityWarning(upgrade: AdminUpgrade): boolean {
  // Warn if a low-stage upgrade has no prerequisites and isn't gated by unlock rules
  // This means it could be bought very late in the game
  if (upgrade.stage_requirement >= 4) return false;
  if (upgrade.prerequisites.length > 0) return false;
  const unlockRules = upgrade.unlock_rules as Record<string, unknown>;
  const hasStatThresholds = unlockRules?.stat_thresholds && Object.keys(unlockRules.stat_thresholds as Record<string, unknown>).length > 0;
  const hasMinLifeChance = typeof unlockRules?.min_life_chance === 'number' && (unlockRules.min_life_chance as number) > 0;
  if (hasStatThresholds || hasMinLifeChance) return false;
  return true;
}

export default function UpgradeManager() {
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(true);
  const [editingUpgrade, setEditingUpgrade] = useState<AdminUpgrade | null>(null);
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'upgrades'],
    queryFn: () => adminApi.getUpgrades(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof adminApi.updateUpgrade>[1] }) =>
      adminApi.updateUpgrade(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'upgrades'] });
      setEditingUpgrade(null);
      setCreating(false);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof adminApi.createUpgrade>[0]) => adminApi.createUpgrade(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'upgrades'] });
      setCreating(false);
    },
  });

  const upgrades = data?.data || [];

  const filtered = useMemo(() => {
    let list = upgrades;
    if (!showInactive) {
      list = list.filter((u) => u.active);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (u) =>
          u.name.toLowerCase().includes(q) ||
          u.description?.toLowerCase().includes(q) ||
          u.category.toLowerCase().includes(q) ||
          u.flavour_text?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [upgrades, showInactive, search]);

  const grouped = useMemo(() => {
    const map = new Map<number, AdminUpgrade[]>();
    for (const u of filtered) {
      const stage = u.stage_requirement;
      if (!map.has(stage)) map.set(stage, []);
      map.get(stage)!.push(u);
    }
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [filtered]);

  const allUpgradesForEdit = upgrades;

  const handleToggleActive = (upgrade: AdminUpgrade) => {
    updateMutation.mutate({ id: upgrade.upgrade_definition_id, data: { active: !upgrade.active } });
  };

  const handleSave = (id: string, data: Parameters<typeof adminApi.updateUpgrade>[1]) => {
    if (creating) {
      createMutation.mutate(data);
    } else {
      updateMutation.mutate({ id, data });
    }
  };

  const emptyUpgrade: AdminUpgrade = {
    upgrade_definition_id: '',
    name: 'New Upgrade',
    description: '',
    category: 'geology',
    stage_requirement: 0,
    cost: 10,
    prerequisites: [],
    effects: {},
    placement_rules: {},
    unlock_rules: {},
    repeatability: 'once',
    max_repeats: 1,
    active: true,
    construction_days: 1,
    daily_effects: {},
    completion_effects: {},
    upkeep_cost: 0,
    conflict_group: null,
    synergy_with: [],
    flavour_text: '',
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-white">Development Definitions</h2>
          <p className="text-xs text-slate-500">Browse, edit, and toggle game upgrades. Yellow warnings highlight potentially ungated early upgrades.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search upgrades..."
              className="w-48 rounded-lg border border-slate-700 bg-slate-800 pl-8 pr-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
            />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-800 text-amber-500"
            />
            Show inactive
          </label>
          <button
            onClick={() => { setCreating(true); setEditingUpgrade(emptyUpgrade); }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-amber-600 hover:bg-amber-500 rounded-lg transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Development
          </button>
        </div>
      </div>

      {/* Loading */}
      {isLoading && <div className="text-sm text-slate-500">Loading upgrades...</div>}

      {/* Empty */}
      {!isLoading && filtered.length === 0 && (
        <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-6 text-center text-sm text-slate-500">
          {upgrades.length === 0 ? 'No upgrades found in database.' : 'No upgrades match your filters.'}
        </div>
      )}

      {/* Stage groups */}
      <div className="space-y-6">
        {grouped.map(([stage, stageUpgrades]) => (
          <div key={stage} className="space-y-2">
            {/* Stage header */}
            <div className={`flex items-center gap-2 pb-1 border-b ${STAGE_COLORS[Math.min(stage, STAGE_COLORS.length - 1)]}`}>
              <span className="text-xs font-bold uppercase tracking-wider">Stage {stage}</span>
              <span className="text-[10px] text-slate-500">{STAGE_NAMES[stage] || ''}</span>
              <span className="text-[10px] text-slate-600 ml-auto">{stageUpgrades.length} upgrade{stageUpgrades.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Upgrade cards */}
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {stageUpgrades.map((upgrade) => {
                const warning = hasLinearityWarning(upgrade);
                const effectTags = summarizeEffects(upgrade.effects as Record<string, unknown>);
                const catColor = CATEGORY_COLORS[upgrade.category] || 'text-slate-400 bg-slate-800 border-slate-700';

                return (
                  <div
                    key={upgrade.upgrade_definition_id}
                    className={`rounded-lg border p-3 transition-all ${
                      upgrade.active
                        ? 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
                        : 'bg-slate-950/40 border-slate-800/50 opacity-60'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border ${catColor}`}>
                            {CATEGORY_ICONS[upgrade.category]}
                            <span className="capitalize">{upgrade.category}</span>
                          </span>
                          {warning && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-yellow-950/50 text-yellow-400 border border-yellow-800/40" title="No prerequisites or stat thresholds — could be bought very late">
                              <AlertTriangle className="w-3 h-3" />
                              Ungated
                            </span>
                          )}
                          {!upgrade.active && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-slate-800 text-slate-400 border border-slate-700">
                              <Lock className="w-3 h-3" />
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-sm font-medium text-white">{upgrade.name}</div>
                        <div className="text-[11px] text-slate-400 leading-snug">{upgrade.description}</div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-sm font-mono font-semibold text-yellow-400">{upgrade.cost}</div>
                        <div className="text-[10px] text-slate-500">credits</div>
                      </div>
                    </div>

                    {/* Prerequisites */}
                    {upgrade.prerequisites.length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-1 text-[10px]">
                        <span className="text-slate-500">Requires:</span>
                        {upgrade.prerequisites.map((prereq, i) => (
                          <span key={prereq.id} className="flex items-center gap-1">
                            {i > 0 && <ArrowRight className="w-2.5 h-2.5 text-slate-600" />}
                            <span className="text-cyan-300 bg-cyan-950/30 px-1.5 py-0.5 rounded">{prereq.name}</span>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Effects summary */}
                    {effectTags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {effectTags.map((tag) => (
                          <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Meta row */}
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-[10px] text-slate-500">
                        <span>{upgrade.construction_days}d build</span>
                        {upgrade.upkeep_cost > 0 && <span>{upgrade.upkeep_cost}cr/day</span>}
                        <span className="capitalize">{upgrade.repeatability}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={upgrade.active}
                            onChange={() => handleToggleActive(upgrade)}
                            className="sr-only peer"
                          />
                          <div className="w-8 h-4 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-emerald-600" />
                        </label>
                        <button
                          onClick={() => { setEditingUpgrade(upgrade); setCreating(false); }}
                          className="p-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                          title="Edit"
                        >
                          <Edit3 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {(editingUpgrade || creating) && (
        <UpgradeEditModal
          upgrade={editingUpgrade || emptyUpgrade}
          allUpgrades={allUpgradesForEdit}
          onSave={handleSave}
          onClose={() => { setEditingUpgrade(null); setCreating(false); }}
          isSaving={updateMutation.isPending || createMutation.isPending}
        />
      )}
    </div>
  );
}
